import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Language,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumns } from '../utils/schemas'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class PointStatsInPolygonsDB extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.pointstatsinpolygons',
        group: 'Aggregation',
        title: 'Point Stats in Polygons',
        description: `
      This component adds new columns with statistics computed from the points of a second table
      that fall within each polygon.
      `
      },
      [
        {
          name: 'points',
          type: WorkflowComponentParamType.InputTable,
          title: 'Points table'
        },
        {
          name: 'polygons',
          type: WorkflowComponentParamType.InputTable,
          title: 'Polygons table'
        },
        {
          name: 'geopoints',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in points table',
          parent: 'points',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'geopolygons',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in polygons table',
          parent: 'polygons',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'statscol',
          type: WorkflowComponentParamType.Column,
          title: 'Column to compute stats for in points table',
          parent: 'points',
          dataType: [ColumnDataType.Number]
        },
        {
          name: 'idcol',
          type: WorkflowComponentParamType.Column,
          title: 'Column with identifier in polygons table',
          parent: 'polygons',
          optional: true,
          dataType: [ColumnDataType.Number, ColumnDataType.String]
        }
      ]
    )
    this.supportedProviders = [Provider.DatabricksRest]
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    let outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const preparedParams = this._prepareParameters(params)

    const leftOutputTableName = connection.quoteName(outputTablename + '_left')
    const rightOutputTableName = connection.quoteName(
      outputTablename + '_right'
    )

    const select = dedent`WITH points AS (
      SELECT /*+ REPARTITION(40, ${preparedParams.geopoints}) */
      *, ${connection.processInputGeogColumn(
        preparedParams.geopoints as string
      )} AS ${preparedParams.geopoints}_processed FROM ${leftOutputTableName}
    ),
    polygons AS (
      SELECT /*+ REPARTITION(40, ${preparedParams.geopolygons}) */
      *, ${connection.processInputGeogColumn(
        preparedParams.geopolygons as string
      )} AS ${preparedParams.geopolygons}_processed FROM ${rightOutputTableName}
    )
    SELECT /*+ REPARTITION(40) */
                polygonsb.*,
                stat.sum_${preparedParams.statscol},
                stat.avg_${preparedParams.statscol},
                stat.max_${preparedParams.statscol},
                stat.min_${preparedParams.statscol}
            FROM
                (SELECT /*+ REPARTITION(40) */
                polygons.${preparedParams.idcol} poligonid,
                SUM(points.${preparedParams.statscol}) AS sum_${
      preparedParams.statscol
    },
                AVG(points.${preparedParams.statscol}) AS avg_${
      preparedParams.statscol
    },
                MIN(points.${preparedParams.statscol}) AS min_${
      preparedParams.statscol
    },
                MAX(points.${preparedParams.statscol}) AS max_${
      preparedParams.statscol
    }
                FROM
                  points,
                  polygons
                WHERE
                    (points.__carto_bounds_xmax >= polygons.__carto_bounds_xmin
                    AND points.__carto_bounds_xmin <= polygons.__carto_bounds_xmax
                    AND points.__carto_bounds_ymax >= polygons.__carto_bounds_ymin
                    AND points.__carto_bounds_ymin <= polygons.__carto_bounds_ymax)
                  AND
                    ST_INTERSECTS(
                      polygons.${preparedParams.geopolygons}_processed,
                      points.${preparedParams.geopoints}_processed
                    )
                GROUP BY
                    polygons.${preparedParams.idcol}
                ) stat
            INNER JOIN ${connection.quoteName(
              preparedParams.polygons as string
            )} polygonsb
            ON stat.poligonid = polygonsb.${preparedParams.idcol}`
    const createTable = (
      connection as DBricksConnection
    ).wrapSQLCodeAsPythonCall(
      connection.getCreateTempTableQuery(
        outputTablename,
        select,
        codeType,
        null
      )
    )
    const generateSpatialFilterTablesQuery = (
      connection as DBricksConnection
    ).generateSpatialFilterTables(
      connection.quoteName(preparedParams.points as string),
      connection.quoteName(preparedParams.polygons as string),
      preparedParams.geopoints as string,
      preparedParams.geopolygons as string,
      leftOutputTableName,
      rightOutputTableName
    )
    const query = `${generateSpatialFilterTablesQuery}\n${createTable}`

    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  language(provider: Provider): Language {
    return Language.Python
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    return addColumns(columns.polygons, [
      {
        name: `sum_${params.statscol}`,
        type: ColumnDataType.Number
      },
      {
        name: `avg_${params.statscol}`,
        type: ColumnDataType.Number
      },
      {
        name: `max_${params.statscol}`,
        type: ColumnDataType.Number
      },
      {
        name: `min_${params.statscol}`,
        type: ColumnDataType.Number
      }
    ])
  }
}

export { PointStatsInPolygonsDB }
